// Long-tail Q&A content targeting problem/question keywords.

export type QuestionLink = { name: string; slug: string };

export type Question = {
  slug: string;
  title: string; // H1
  metaTitle: string;
  metaDescription: string;
  intro: string;
  sections: { h: string; p: string[] }[];
  bullets?: { h: string; items: string[] };
  relatedClauses: QuestionLink[];
  relatedTools: QuestionLink[];
  relatedQuestions: QuestionLink[];
};

export const QUESTIONS: Question[] = [
  {
    slug: "what-should-i-look-for-in-a-lease",
    title: "What Should I Look for in a Lease Before I Sign?",
    metaTitle: "What to Look for in a Lease — Renter's Checklist",
    metaDescription:
      "The 8 lease clauses that cost renters real money — security deposit, auto-renewal, late fees, entry notice, and more. Plain-English checklist.",
    intro:
      "Most renters spend less than 10 minutes reading their lease and then commit to $20,000+ in rent. Here are the 8 things to actually read.",
    sections: [
      {
        h: "1. Security deposit",
        p: [
          "How much, where it's held, and when you get it back. Most states cap deposits at 1–2 months' rent and require return within 14–30 days with itemized deductions.",
        ],
      },
      {
        h: "2. Auto-renewal",
        p: [
          "If you don't give notice by a specific date (often 60 days before expiration), the lease can renew automatically — sometimes at a landlord-set rate. Calendar the deadline.",
        ],
      },
      {
        h: "3. Early termination",
        p: [
          "What does it cost to leave early? A flat 1–2 months' rent fee is fair. 'Owe all remaining rent' is not.",
        ],
      },
      {
        h: "4. Late fees and grace period",
        p: [
          "A 3–5 day grace period and a flat or capped percentage fee is normal. Daily compounding fees may be illegal in your state.",
        ],
      },
      {
        h: "5. Landlord entry",
        p: ["24–48 hours written notice is standard. 'Enter at any time' is a privacy red flag."],
      },
      {
        h: "6. Maintenance and repairs",
        p: [
          "Major systems (HVAC, plumbing, appliances) should be the landlord's responsibility, with a defined response time.",
        ],
      },
      {
        h: "7. Pet, guest, and roommate rules",
        p: [
          "Pet deposits, monthly pet rent, overnight-guest limits, and roommate-replacement rules can all become real problems.",
        ],
      },
      {
        h: "8. Disclosures and lead/asbestos",
        p: [
          "For pre-1978 buildings, lead-based paint disclosure is federally required. Confirm any state-specific disclosures (mold, bedbugs, flood zone).",
        ],
      },
    ],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
      { name: "Early Termination Clause", slug: "early-termination-clause" },
      { name: "Late Fees Clause", slug: "late-fees-clause" },
      { name: "Entry Notice Clause", slug: "entry-notice-clause" },
    ],
    relatedTools: [{ name: "Lease Decoder", slug: "lease-decoder" }],
    relatedQuestions: [
      { name: "Can I negotiate a lease clause?", slug: "can-i-negotiate-a-lease-clause" },
      { name: "How to spot unfair contract terms", slug: "how-to-spot-unfair-contract-terms" },
    ],
  },
  {
    slug: "what-is-a-bad-nda",
    title: "What Is a Bad NDA? 7 Red Flags Before You Sign",
    metaTitle: "What Is a Bad NDA? 7 Red Flags — legaldecoder",
    metaDescription:
      "A bad NDA can silence you for life or hide a non-compete. Here are the 7 red flags every employee, founder, and contractor should know.",
    intro: "Most NDAs are reasonable. The bad ones share the same handful of warning signs.",
    sections: [
      {
        h: "1. 'Any information of any kind'",
        p: [
          "Reasonable NDAs define confidential information narrowly — specifically marked materials or clearly proprietary data. Catch-all language is overbroad and often unenforceable.",
        ],
      },
      {
        h: "2. Perpetual duration",
        p: [
          "Standard is 2–5 years after the relationship ends. 'In perpetuity' is a gag order — push for a sunset.",
        ],
      },
      {
        h: "3. Hidden non-compete",
        p: [
          "A confidentiality clause that says you can't work for competitors is a non-compete in disguise. Strike it.",
        ],
      },
      {
        h: "4. No standard exceptions",
        p: [
          "Every fair NDA carves out information that is publicly known, that you knew before, that you developed independently, or that's required by court order.",
        ],
      },
      {
        h: "5. Tries to silence harassment",
        p: [
          "The federal Speak Out Act (2022) voids forced silence on sexual harassment and assault claims. State laws (CA, NY, NJ) go further.",
        ],
      },
      {
        h: "6. One-sided remedies",
        p: [
          "Liquidated damages, attorney's fees only flowing one way, or injunctive relief without bond — all give the other side disproportionate leverage.",
        ],
      },
      {
        h: "7. No carve-out for whistleblowers",
        p: [
          "Federal law protects disclosure of suspected illegal activity to government agencies. Your NDA should acknowledge this — many don't.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
    relatedTools: [{ name: "NDA Scanner", slug: "nda-scanner" }],
    relatedQuestions: [
      { name: "Is this NDA normal?", slug: "is-this-nda-normal" },
      {
        name: "Should I sign this freelance contract?",
        slug: "should-i-sign-this-freelance-contract",
      },
    ],
  },
  {
    slug: "how-to-read-a-contract",
    title: "How to Read a Contract (Without Being a Lawyer)",
    metaTitle: "How to Read a Contract — Plain-English Guide",
    metaDescription:
      "A simple 6-step method for reading any contract — what to skip, what to highlight, and the clauses where the money actually lives.",
    intro:
      "Reading a contract isn't about understanding every word. It's about finding the 6 sections where the money and the risk live.",
    sections: [
      {
        h: "Step 1: Skim the structure first",
        p: [
          "Identify section headers. Most contracts follow the same skeleton: parties, scope, payment, IP, confidentiality, term & termination, indemnification, limitation of liability, and miscellaneous.",
        ],
      },
      {
        h: "Step 2: Read the money sections in full",
        p: [
          "Payment terms, late fees, kill fees, deposit refund — these are where you win or lose the deal in real numbers.",
        ],
      },
      {
        h: "Step 3: Find every 'sole discretion'",
        p: [
          "If the other side has 'sole discretion' over anything (deductions, deliverables, termination), they effectively rewrite that clause later. Replace with 'reasonable' or 'mutual.'",
        ],
      },
      {
        h: "Step 4: Check duration and notice everywhere",
        p: [
          "Auto-renewal windows, termination notice, dispute resolution windows, payment cycles. Calendar every deadline before you sign.",
        ],
      },
      {
        h: "Step 5: Read the indemnity and liability cap",
        p: [
          "These two paragraphs decide what happens when things go wrong. One-sided uncapped indemnity is the single biggest hidden risk in contractor agreements.",
        ],
      },
      {
        h: "Step 6: Look for what's missing",
        p: [
          "No severance? No kill fee? No notice on termination? Silence isn't safety — it's the default rule, which usually favors the more powerful party.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Payment Terms Clause", slug: "payment-terms-clause" },
    ],
    relatedTools: [
      { name: "Lease Decoder", slug: "lease-decoder" },
      { name: "Contractor Agreement Checker", slug: "contractor-agreement-checker" },
    ],
    relatedQuestions: [
      { name: "How to spot unfair contract terms", slug: "how-to-spot-unfair-contract-terms" },
      { name: "What does this clause mean?", slug: "what-does-this-clause-mean" },
    ],
  },
  {
    slug: "how-to-spot-unfair-contract-terms",
    title: "How to Spot Unfair Contract Terms",
    metaTitle: "How to Spot Unfair Contract Terms — legaldecoder",
    metaDescription:
      "The 10 most common unfair clauses in everyday contracts — what they look like, why they're a problem, and how to fix them.",
    intro:
      "Unfair terms hide behind polite legalese. Here's how to recognize them in any contract.",
    sections: [
      {
        h: "Asymmetry",
        p: [
          "If only one side has a right (to terminate, to assign, to amend), that's the first red flag. Negotiate for mutual obligations.",
        ],
      },
      {
        h: "Vague triggers",
        p: [
          "'As reasonably requested,' 'in the company's sole discretion,' 'related deliverables.' These phrases turn defined obligations into open-ended ones.",
        ],
      },
      {
        h: "Uncapped exposure",
        p: [
          "No cap on indemnity, no cap on damages, no cap on rent renewal. The absence of a number is itself a problem.",
        ],
      },
      {
        h: "Hidden timelines",
        p: [
          "Auto-renewal notice windows, dispute deadlines, termination notice — if you miss them, you lose the right.",
        ],
      },
      {
        h: "Forfeiture clauses",
        p: [
          "Lose your deposit, your equity, or your earned bonus on the wrong move. Push for explicit carve-outs.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
    ],
    relatedTools: [
      { name: "Lease Decoder", slug: "lease-decoder" },
      { name: "Freelancer Protector", slug: "freelancer-protector" },
    ],
    relatedQuestions: [
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
      { name: "What are red flags in a contract?", slug: "what-are-red-flags-in-a-contract" },
    ],
  },
  {
    slug: "can-i-negotiate-a-lease-clause",
    title: "Can I Negotiate a Lease Clause? Yes — Here's What's Actually on the Table",
    metaTitle: "Can I Negotiate a Lease Clause? — legaldecoder",
    metaDescription:
      "Most renters never negotiate. Here are the 6 lease clauses landlords routinely concede — and exactly how to ask.",
    intro:
      "Landlords expect negotiation. Most renters never ask. Here are the 6 clauses they routinely concede.",
    sections: [
      {
        h: "1. Security deposit amount",
        p: [
          "If your state cap is 2 months but the lease says 2 months, ask for 1. A clean rental history and proof of income often gets it.",
        ],
      },
      {
        h: "2. Auto-renewal terms",
        p: [
          "Convert auto-renewal to month-to-month after the initial term, and ask for a written reminder 60 days before any renewal deadline.",
        ],
      },
      {
        h: "3. Early termination fee",
        p: [
          "Aim for a flat 1–2 months' rent termination fee instead of 'rent until re-rented.' Add a job-relocation or military exception.",
        ],
      },
      {
        h: "4. Pet fees",
        p: [
          "Pet deposits should be refundable. Push back on 'non-refundable pet fee + monthly pet rent + pet deposit' stacking.",
        ],
      },
      {
        h: "5. Renewal rent cap",
        p: [
          "Cap rent increases at renewal at 5% or local CPI — landlords often agree in writing to retain a good tenant.",
        ],
      },
      {
        h: "6. Move-in inspection",
        p: [
          "Demand a written, photographed move-in condition report attached to the lease. This protects your deposit at move-out.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
      { name: "Early Termination Clause", slug: "early-termination-clause" },
    ],
    relatedTools: [{ name: "Lease Decoder", slug: "lease-decoder" }],
    relatedQuestions: [
      { name: "What should I look for in a lease?", slug: "what-should-i-look-for-in-a-lease" },
      { name: "What are red flags in a contract?", slug: "what-are-red-flags-in-a-contract" },
    ],
  },
  {
    slug: "is-this-nda-normal",
    title: "Is This NDA Normal? How to Tell in 60 Seconds",
    metaTitle: "Is This NDA Normal? — legaldecoder",
    metaDescription:
      "A 60-second checklist for telling whether an NDA is industry-standard or unusually aggressive — duration, scope, mutuality, remedies.",
    intro:
      "Use this 60-second checklist to gauge whether an NDA is reasonable or worth negotiating.",
    sections: [
      {
        h: "Duration",
        p: ["Normal: 2–5 years post-relationship. Concerning: 'in perpetuity.'"],
      },
      {
        h: "Scope",
        p: [
          "Normal: marked or clearly proprietary information. Concerning: 'all information of any kind.'",
        ],
      },
      {
        h: "Mutuality",
        p: [
          "If you'll share sensitive info too, the NDA should be mutual. One-sided is fine for hire / vendor work.",
        ],
      },
      {
        h: "Standard exceptions",
        p: [
          "Public domain, prior knowledge, independent development, court-ordered disclosure. Missing all four is a red flag.",
        ],
      },
      {
        h: "Hidden non-compete",
        p: ["Watch for restrictions on where you can work next. That's a non-compete, not an NDA."],
      },
    ],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
    ],
    relatedTools: [{ name: "NDA Scanner", slug: "nda-scanner" }],
    relatedQuestions: [
      { name: "What is a bad NDA?", slug: "what-is-a-bad-nda" },
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
    ],
  },
  {
    slug: "should-i-sign-this-freelance-contract",
    title: "Should I Sign This Freelance Contract? A 5-Minute Audit",
    metaTitle: "Should I Sign This Freelance Contract? — legaldecoder",
    metaDescription:
      "A fast 5-minute audit for any freelance contract: payment, IP, indemnity, termination, and kill fee. With go / no-go thresholds.",
    intro: "Use this 5-minute audit to decide whether to sign, negotiate, or walk.",
    sections: [
      {
        h: "Payment terms",
        p: [
          "Sign: net-15 to net-30 with deposit. Negotiate: net-60. Walk: net-90 with no deposit and no late fee.",
        ],
      },
      {
        h: "IP transfer",
        p: [
          "Sign: transfer on final payment. Negotiate: transfer at signing with milestone deposits. Walk: full transfer with net-90 payment.",
        ],
      },
      {
        h: "Indemnification",
        p: [
          "Sign: mutual, capped at fees paid. Negotiate: one-sided but capped. Walk: uncapped, one-sided, includes 'all claims.'",
        ],
      },
      {
        h: "Termination",
        p: [
          "Sign: 14–30 days notice + payment for work in progress. Negotiate: notice with no kill fee. Walk: same-day cancellation with no payment.",
        ],
      },
      {
        h: "Kill fee",
        p: [
          "Sign: 25–50% of remaining contract. Negotiate: scaled by milestone. Walk: no kill fee on a multi-month engagement.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Payment Terms Clause", slug: "payment-terms-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Kill Fee Clause", slug: "kill-fee-clause" },
    ],
    relatedTools: [
      { name: "Freelancer Protector", slug: "freelancer-protector" },
      { name: "Contractor Agreement Checker", slug: "contractor-agreement-checker" },
    ],
    relatedQuestions: [
      { name: "What are red flags in a contract?", slug: "what-are-red-flags-in-a-contract" },
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
    ],
  },
  {
    slug: "what-does-this-clause-mean",
    title: "What Does This Clause Mean? A Plain-English Translator",
    metaTitle: "What Does This Clause Mean? — legaldecoder",
    metaDescription:
      "Translate the 10 most common contract clauses from legalese to plain English — with sample language and what to push back on.",
    intro:
      "Most legalese is a handful of phrases recycled across millions of contracts. Here's the translation.",
    sections: [
      {
        h: "'Indemnify, defend, and hold harmless'",
        p: ["You pay the other side's legal bills if anything goes wrong. Cap it. Make it mutual."],
      },
      {
        h: "'Sole discretion'",
        p: [
          "The other side decides — and you have no recourse. Replace with 'reasonable' or 'mutual.'",
        ],
      },
      {
        h: "'In perpetuity'",
        p: ["Forever. For NDAs, push for 2–5 years."],
      },
      {
        h: "'Force majeure'",
        p: [
          "Events outside reasonable control — war, natural disaster, pandemic, government order — that excuse performance.",
        ],
      },
      {
        h: "'Liquidated damages'",
        p: [
          "A pre-agreed dollar amount owed on breach, instead of proving actual damages. Read carefully — it's a cap and a floor.",
        ],
      },
      {
        h: "'Time is of the essence'",
        p: [
          "Deadlines are material. Missing one is a breach. Don't agree without realistic dates.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Force Majeure Clause", slug: "force-majeure-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
    ],
    relatedTools: [
      { name: "Contractor Agreement Checker", slug: "contractor-agreement-checker" },
      { name: "Employment Agreement Checker", slug: "employment-agreement-checker" },
    ],
    relatedQuestions: [
      { name: "How to understand legal jargon", slug: "how-to-understand-legal-jargon" },
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
    ],
  },
  {
    slug: "how-to-understand-legal-jargon",
    title: "How to Understand Legal Jargon (A Pocket Glossary)",
    metaTitle: "How to Understand Legal Jargon — legaldecoder",
    metaDescription:
      "A pocket glossary of the 20 legalese terms that appear in almost every contract — with plain-English equivalents.",
    intro:
      "Most legalese is recycled vocabulary. Learn 20 terms and 80% of contracts become readable.",
    sections: [
      {
        h: "Parties",
        p: [
          "The people or companies signing — usually defined as 'Party' or by short names like 'Client' and 'Contractor.'",
        ],
      },
      {
        h: "Recitals / 'Whereas'",
        p: ["Background context. Not legally binding, but useful for interpretation."],
      },
      {
        h: "Consideration",
        p: [
          "What each side gives — money, services, promises. Required to make a contract enforceable.",
        ],
      },
      {
        h: "Material breach",
        p: ["A serious failure that justifies termination — vs. a 'minor' breach you can fix."],
      },
      {
        h: "Severability",
        p: ["If one clause is void, the rest still apply. Standard boilerplate."],
      },
      {
        h: "Boilerplate",
        p: [
          "Standard miscellaneous provisions at the end — assignment, notices, governing law. Read them anyway.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Assignment Clause", slug: "assignment-clause" },
      { name: "Jurisdiction Clause", slug: "jurisdiction-clause" },
    ],
    relatedTools: [
      { name: "Lease Decoder", slug: "lease-decoder" },
      { name: "NDA Scanner", slug: "nda-scanner" },
    ],
    relatedQuestions: [
      { name: "What does this clause mean?", slug: "what-does-this-clause-mean" },
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
    ],
  },
  {
    slug: "what-are-red-flags-in-a-contract",
    title: "What Are Red Flags in a Contract? The Top 10",
    metaTitle: "Top 10 Contract Red Flags — legaldecoder",
    metaDescription:
      "The 10 contract red flags that show up across leases, NDAs, employment offers, and freelance agreements — and how to push back.",
    intro:
      "Different contracts, same warning signs. Here are the 10 red flags that span every category.",
    sections: [
      {
        h: "1. 'Sole discretion' anywhere",
        p: ["The other side rewrites the rule later. Replace with 'reasonable.'"],
      },
      {
        h: "2. One-sided rights",
        p: ["Only they can terminate, assign, or amend. Push for mutual."],
      },
      {
        h: "3. Uncapped indemnification",
        p: ["You pay their legal bills with no ceiling. Cap at fees paid; make it mutual."],
      },
      {
        h: "4. Auto-renewal with no reminder",
        p: ["Miss the date, owe another year. Demand written reminder + cap on rate."],
      },
      {
        h: "5. Forced arbitration with class waiver",
        p: ["Limits remedies and prevents joining other employees. Strike if you can."],
      },
      {
        h: "6. Perpetual confidentiality",
        p: ["Lifetime gag. Cap at 2–5 years."],
      },
      {
        h: "7. IP transfer before payment",
        p: ["You can deliver and never get paid. Tie transfer to final payment."],
      },
      {
        h: "8. 'As reasonably requested'",
        p: ["Open-ended scope. Replace with a fixed deliverables list and change-order pricing."],
      },
      {
        h: "9. Forfeiture of deposits / equity",
        p: ["Lose what you've earned on a technicality. Add explicit carve-outs."],
      },
      {
        h: "10. Distant jurisdiction",
        p: [
          "Litigation in their state, on their dime. Negotiate your home state or a neutral one.",
        ],
      },
    ],
    relatedClauses: [
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Arbitration Clause", slug: "arbitration-clause" },
      { name: "Auto-Renewal (Subscription) Clause", slug: "auto-renewal-subscription-clause" },
    ],
    relatedTools: [
      { name: "Lease Decoder", slug: "lease-decoder" },
      { name: "Employment Agreement Checker", slug: "employment-agreement-checker" },
      { name: "Contractor Agreement Checker", slug: "contractor-agreement-checker" },
    ],
    relatedQuestions: [
      { name: "How to spot unfair contract terms", slug: "how-to-spot-unfair-contract-terms" },
      { name: "How to read a contract", slug: "how-to-read-a-contract" },
    ],
  },
];

export const QUESTIONS_BY_SLUG: Record<string, Question> = Object.fromEntries(
  QUESTIONS.map((q) => [q.slug, q]),
);
